import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  ListSubheader,
  InputAdornment,
  SelectChangeEvent,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MoneyIcon,
  BarChart as ChartIcon,
  AttachMoney as AttachMoneyIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { Metric, MetricGroupedCategories, metricGroupedCategories, EditedData } from '@features/deals/types';
import MetricsTable from '@features/deals/components/WizardShared/MetricsTable';
import DealFinancialsTables from '@features/deals/components/WizardShared/DealFinancialsTables';
import { api } from '@services';
import { useParams } from 'react-router-dom';

// Function to generate mock financial data for demonstration/development purposes
const generateMockFinancialData = (): Metric[] => {
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  // Sample categories and values
  const categories = [
    { category: 'Revenue', item: 'Total Revenue', baseValue: 5000000 },
    { category: 'Revenue', item: 'Room Revenue', baseValue: 3500000 },
    { category: 'Revenue', item: 'F&B Revenue', baseValue: 1000000 },
    { category: 'Expenses', item: 'Total Expenses', baseValue: 3000000 },
    { category: 'Expenses', item: 'Labor Costs', baseValue: 1500000 },
    { category: 'NOI', item: 'Net Operating Income', baseValue: 2000000 },
    { category: 'Occupancy Rate', item: 'Occupancy Rate', baseValue: 75 },
    { category: 'ADR', item: 'Average Daily Rate', baseValue: 120 },
    { category: 'RevPAR', item: 'Revenue Per Available Room', baseValue: 95 }
  ];

  // Generate metrics with yearly growth
  const mockMetrics: Metric[] = [];

  years.forEach(year => {
    // Older years have slightly lower values (simulating growth)
    const yearOffset = currentYear - year;
    const growthFactor = 1 - (yearOffset * 0.05); // 5% less per year back

    categories.forEach(({ category, item, baseValue }) => {
      // Adjust value based on year with some random variation
      const randomFactor = 0.9 + (Math.random() * 0.2); // ±10% random variation
      const adjustedValue = baseValue * growthFactor * randomFactor;

      // Create a metric object using only fields from the Metric interface
      mockMetrics.push({
        category,
        item,
        year,
        value: Math.round(adjustedValue), // Round to whole number
        source: 'manual', // Indicate this is manually generated data
        readOnly: false // Allow editing
      });
    });
  });

  return mockMetrics;
};

interface FinancialDataStepProps {
  metrics: Metric[];
  setMetrics: React.Dispatch<React.SetStateAction<Metric[]>>;
  editedData: EditedData;
  setEditedData: React.Dispatch<React.SetStateAction<EditedData>>;
  deletedRows: Set<string>;
  setDeletedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedCategory: string;
  setSelectedCategory: React.Dispatch<React.SetStateAction<string>>;
  newItemName: string;
  setNewItemName: React.Dispatch<React.SetStateAction<string>>;
  newItemYear: string;
  setNewItemYear: React.Dispatch<React.SetStateAction<string>>;
  newItemAmount: string;
  setNewItemAmount: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  success: string | null;
  setSuccess: React.Dispatch<React.SetStateAction<string | null>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  handleAddNewItem: () => void;
}

// Define tab interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

// Format currency helper
const formatCurrency = (value: number | string | undefined): string => {
  if (value === undefined) return '$0';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return numValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

// Format percent helper
const formatPercent = (value: number | string | undefined): string => {
  if (value === undefined) return '0%';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return `${numValue.toFixed(1)}%`;
};

const FinancialDataStep: React.FC<FinancialDataStepProps> = ({
  metrics,
  setMetrics,
  editedData,
  setEditedData,
  deletedRows,
  setDeletedRows,
  selectedCategory,
  setSelectedCategory,
  newItemName,
  setNewItemName,
  newItemYear,
  setNewItemYear,
  newItemAmount,
  setNewItemAmount,
  loading,
  success,
  setSuccess,
  error,
  setError,
  handleAddNewItem
}) => {
  const theme = useTheme();
  // Get unique years from metrics for table display
  const tableYears = Array.from(new Set(metrics.map((m) => m.year))).sort();
  const [tabValue, setTabValue] = useState(0);
  const { hotelId } = useParams<{ hotelId: string }>();
  const [isLoadingFinancials, setIsLoadingFinancials] = useState(false);

  // Modify the loadFinancialData function to use mock data as fallback
  useEffect(() => {
    const loadFinancialData = async () => {
      if (!hotelId) {
        console.error('Cannot fetch financial data: hotelId is missing or undefined');
        setError('No hotel selected. Please select a hotel first.');
        setIsLoadingFinancials(false);
        return;
      }

      try {
        setIsLoadingFinancials(true);
        console.log('Loading financial data for hotel:', hotelId);
        console.log('Current URL:', window.location.href);

        // Check if authentication token exists
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No authentication token found - auth may fail');
          setError('Authentication issue. Please log in again.');
          setIsLoadingFinancials(false);
          return;
        }
        console.log('Auth token exists:', !!token, 'Length:', token.length);

        // Additional debug info
        console.log('Current metrics before API call:', metrics.length > 0 ? 'Has data' : 'Empty');

        // Use our new comprehensive API method
        console.log('API call starting: getHotelFinancialData');
        const financialData = await api.getHotelFinancialData(hotelId);
        console.log('API call finished. Response source:', financialData.source);
        console.log('API call data length:', financialData.data ? financialData.data.length : 0);
        console.log(`Financial data loaded from source: ${financialData.source}`, financialData.data);

        if (financialData.data && financialData.data.length > 0) {
          console.log('Raw financial data:', JSON.stringify(financialData.data).substring(0, 500) + '...');

          // Transform API data to metric format if needed - add index to preserve order
          const financialMetrics = financialData.data.map((item: any, index: number) => ({
            id: item.id || `${item.category || 'unknown'}-${item.year}-${Math.random().toString(36)}`,
            category: item.category || 'Unknown',
            item: item.item || item.name || '',
            year: typeof item.year === 'string' ? parseInt(item.year, 10) : (item.year || new Date().getFullYear()),
            value: typeof item.value === 'string' ? parseFloat(item.value) : (item.value || 0),
            index: index // Add index to help with editing
          }));

          console.log('Transformed metrics:', JSON.stringify(financialMetrics).substring(0, 500) + '...');
          console.log(`Transformed ${financialData.data.length} items into ${financialMetrics.length} metrics`);

          // Set metrics regardless of whether current array is empty (the user mentioned data shows elsewhere)
          console.log('Setting metrics from financial data:', financialMetrics);
          setMetrics(financialMetrics);

          // Set available years for the dropdown
          const years = Array.from(new Set(financialMetrics.map((m: { year: number }) => m.year))).sort() as number[];
          console.log('Years extracted:', years);
          if (years.length > 0 && !newItemYear) {
            setNewItemYear(years[0].toString());
          }
        } else {
          console.log('No financial data found for this hotel. Using mock data for development.');
          // Use mock data since the API returned empty data
          const mockMetrics = generateMockFinancialData();
          console.log('Generated mock metrics:', mockMetrics);
          setMetrics(mockMetrics);

          // Set available years from mock data
          const years = Array.from(new Set(mockMetrics.map(m => m.year))).sort() as number[];
          if (years.length > 0 && !newItemYear) {
            setNewItemYear(years[0].toString());
          }
        }
      } catch (err) {
        console.error('Error loading financial data:', err);
        // More detailed error logging
        if (err instanceof Error) {
          console.error('Error message:', err.message);
          console.error('Error stack:', err.stack);
        }

        // Check for specific auth errors
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as any;
          if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
            setError('Authentication failed. Please log in again.');
            console.error('Auth error details:', axiosError.response.data);

            // Use mock data even on auth failure for development purposes
            console.log('Using mock data due to auth failure');
            const mockMetrics = generateMockFinancialData();
            setMetrics(mockMetrics);

            // Set available years from mock data
            const years = Array.from(new Set(mockMetrics.map(m => m.year))).sort() as number[];
            if (years.length > 0 && !newItemYear) {
              setNewItemYear(years[0].toString());
            }
            return;
          }
        }

        setError('Failed to load financial data. Please try again.');

        // Fallback to mock data on any error
        console.log('Using mock data due to error');
        const mockMetrics = generateMockFinancialData();
        setMetrics(mockMetrics);

        // Set available years from mock data
        const years = Array.from(new Set(mockMetrics.map(m => m.year))).sort() as number[];
        if (years.length > 0 && !newItemYear) {
          setNewItemYear(years[0].toString());
        }
      } finally {
        setIsLoadingFinancials(false);
      }
    };

    loadFinancialData();
  }, [hotelId, setMetrics, setError, newItemYear, setNewItemYear, metrics.length]);

  // Group metrics by category for better organization
  const metricsByCategory = metrics.reduce<{[key: string]: Metric[]}>((acc, metric) => {
    const mainCategory = Object.keys(metricGroupedCategories).find(
      group => metricGroupedCategories[group].items.includes(metric.category) ||
              metric.category.includes(group)
    ) || 'Other';

    if (!acc[mainCategory]) {
      acc[mainCategory] = [];
    }

    acc[mainCategory].push(metric);
    return acc;
  }, {});

  // Calculate financial summary data
  const financialSummary = useMemo(() => {
    // Get the most recent year
    const years = metrics.map(m => m.year);
    const mostRecentYear = years.length > 0 ? Math.max(...years) : new Date().getFullYear();

    // Filter metrics for the most recent year
    const recentMetrics = metrics.filter(m => m.year === mostRecentYear);

    // More flexible matching for key financial metrics
    const findMetric = (keywords: string[], itemKeywords: string[] = []) => {
      // Try to find an exact match first
      let match = recentMetrics.find(m =>
        keywords.some(k => m.category.toLowerCase() === k.toLowerCase()) &&
        (itemKeywords.length === 0 || itemKeywords.some(k => m.item.toLowerCase() === k.toLowerCase()))
      );

      // If no exact match, try partial match
      if (!match) {
        match = recentMetrics.find(m =>
          keywords.some(k => m.category.toLowerCase().includes(k.toLowerCase())) &&
          (itemKeywords.length === 0 || itemKeywords.some(k => m.item.toLowerCase().includes(k.toLowerCase())))
        );
      }

      return match;
    };

    // Extract metrics with more flexible matching
    const revenue = findMetric(['revenue', 'total revenue'], ['total', 'room revenue']) ||
                   findMetric(['revenue']);

    const expenses = findMetric(['expenses', 'total expenses'], ['total']) ||
                    findMetric(['expenses']);

    const noi = findMetric(['noi', 'net operating income']) ||
               findMetric(['income'], ['net', 'operating']);

    const occupancy = findMetric(['occupancy rate', 'occupancy']) ||
                     findMetric(['kpi'], ['occupancy']);

    const adr = findMetric(['adr', 'average daily rate']) ||
               findMetric(['daily rate', 'average']);

    const revpar = findMetric(['revpar', 'revenue per available room']) ||
                  findMetric(['revenue per room', 'rev par']);

    // Extract values safely
    const extractValue = (metric: Metric | undefined) => {
      if (!metric) return undefined;
      if (typeof metric.value === 'string') {
        const numValue = parseFloat(metric.value.replace(/[^0-9.-]/g, ''));
        return isNaN(numValue) ? undefined : numValue;
      }
      return metric.value;
    };

    const revenueValue = extractValue(revenue);
    const expensesValue = extractValue(expenses);
    const noiValue = extractValue(noi);
    const occupancyValue = extractValue(occupancy);
    const adrValue = extractValue(adr);
    const revparValue = extractValue(revpar);

    // Calculate profit margin if we have revenue and NOI
    let profitMargin;
    if (revenueValue && noiValue && revenueValue > 0) {
      profitMargin = (noiValue / revenueValue) * 100;
    }

    // Log the found metrics to help with debugging
    console.log('Financial Summary Metrics:', {
      year: mostRecentYear,
      revenue: revenue?.category,
      expenses: expenses?.category,
      noi: noi?.category,
      occupancy: occupancy?.category,
      adr: adr?.category,
      revpar: revpar?.category,
      revenueValue,
      noiValue,
      profitMargin
    });

    return {
      revenue: revenueValue,
      expenses: expensesValue,
      netOperatingIncome: noiValue,
      occupancyRate: occupancyValue,
      adr: adrValue,
      revpar: revparValue,
      profitMargin,
      year: mostRecentYear
    };
  }, [metrics]);

  // Debug logging for metrics data
  useEffect(() => {
    if (metrics.length > 0) {
      console.log('Available metrics:', metrics.map(m => ({
        category: m.category,
        item: m.item,
        year: m.year,
        value: m.value
      })));
    }
  }, [metrics]);

  // Check data completeness for tab indicators
  const dataCompleteness = useMemo(() => {
    // Data categories we want to check for
    const categories = {
      kpi: ['occupancy', 'adr', 'revpar'],
      revenue: ['revenue', 'total revenue'],
      expense: ['expense', 'total expense', 'expenses', 'total expenses'],
      noi: ['noi', 'net operating income']
    };

    // Check if we have any metrics in each category
    const hasData = (categoryTerms: string[]) => {
      return metrics.some(m =>
        categoryTerms.some(term =>
          m.category.toLowerCase().includes(term.toLowerCase()) ||
          (m.item && m.item.toLowerCase().includes(term.toLowerCase()))
        )
      );
    };

    return {
      kpi: hasData(categories.kpi),
      revenue: hasData(categories.revenue),
      expense: hasData(categories.expense),
      noi: hasData(categories.noi),
      // If we have all three core financial areas, consider it complete
      complete: hasData(categories.revenue) && hasData(categories.expense) && hasData(categories.noi)
    };
  }, [metrics]);

  const handleValueChange = (index: number, category: string, year: string, value: string) => {
    setEditedData((prev) => {
      const newData = { ...prev };

      if (!newData[index]) {
        newData[index] = {};
      }

      if (!newData[index][category]) {
        newData[index][category] = {};
      }

      if (value === '') {
        newData[index][category][year] = '0';
      } else {
        newData[index][category][year] = value;
      }

      // Clean up empty objects
      if (Object.keys(newData[index][category]).length === 0) {
        delete newData[index][category];
      }
      if (Object.keys(newData[index]).length === 0) {
        delete newData[index];
      }

      return newData;
    });
  };

  const handleDeleteItem = (index: number, category: string) => {
    const metricToDelete = metrics[index];
    if (!metricToDelete) return;

    // Create a more reliable unique identifier for tracking deletions
    // Format: "category-item-year" instead of just including the index which changes after deletions
    const uniqueId = `${metricToDelete.category}-${metricToDelete.item || ''}-${metricToDelete.year}`;

    // Track deleted item in state
    setDeletedRows((prev) => new Set([...prev, uniqueId]));

    // Remove from metrics array
    setMetrics((prevMetrics) => prevMetrics.filter((_, idx) => idx !== index));

    setSuccess('Item removed successfully');
    setError(null);
  };

  const handleCategorySelect = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedCategory(value);
    if (value !== '') {
      setError(null);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Add a debug log in the component to check metrics being passed
  useEffect(() => {
    console.log('FinancialDataStep metrics data:', metrics);
    console.log('tableYears:', tableYears);
  }, [metrics, tableYears]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Financial Data
      </Typography>

      {isLoadingFinancials && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Loading financial data...
        </Alert>
      )}

      {/* Dashboard Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Revenue */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="textSecondary">Revenue</Typography>
              </Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {financialSummary.revenue !== undefined
                  ? formatCurrency(financialSummary.revenue)
                  : 'No data'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {financialSummary.year
                  ? `FY ${financialSummary.year}`
                  : 'Add "Total Revenue" metric'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* NOI */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttachMoneyIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
                <Typography variant="subtitle2" color="textSecondary">Net Operating Income</Typography>
              </Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {financialSummary.netOperatingIncome !== undefined
                  ? formatCurrency(financialSummary.netOperatingIncome)
                  : 'No data'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {financialSummary.year
                  ? `FY ${financialSummary.year}`
                  : 'Add "NOI" metric'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Occupancy Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SpeedIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="textSecondary">Occupancy Rate</Typography>
              </Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {financialSummary.occupancyRate !== undefined
                  ? formatPercent(financialSummary.occupancyRate)
                  : 'No data'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {financialSummary.year
                  ? `FY ${financialSummary.year}`
                  : 'Add "Occupancy Rate" metric'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* RevPAR */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ChartIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="textSecondary">RevPAR</Typography>
              </Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {financialSummary.revpar !== undefined
                  ? formatCurrency(financialSummary.revpar)
                  : 'No data'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {financialSummary.year
                  ? `FY ${financialSummary.year}`
                  : 'Add "RevPAR" metric'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="financial data tabs"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Data Entry" />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              Financial Tables
              {dataCompleteness.complete ? (
                <Chip
                  size="small"
                  label="Complete"
                  color="success"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              ) : (
                <Chip
                  size="small"
                  label="Incomplete"
                  color="warning"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          }
        />
        <Tab label="Financial Summary" />
        <Tab label="Categorized View" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }} error={!selectedCategory}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={handleCategorySelect}
              label="Category"
              variant="standard"
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select a category</em>
              </MenuItem>
              {Object.entries(metricGroupedCategories)
                .sort(([, a], [, b]) => a.order - b.order)
                .map(([group, { items }]) => [
                  <ListSubheader key={group}>{group}</ListSubheader>,
                  ...items.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  )),
                ])}
            </Select>
          </FormControl>

          <TextField
            error={!!newItemName && newItemName.trim().length === 0}
            helperText={newItemName && newItemName.trim().length === 0 ? 'Item name cannot be empty' : ''}
            size="small"
            label="New Item Name"
            value={newItemName}
            onChange={(e) => {
              setNewItemName(e.target.value);
              if (e.target.value.trim().length > 0) {
                setError(null);
              }
            }}
            sx={{ flexGrow: 1 }}
            variant="standard"
          />

          <FormControl sx={{ width: 120 }} error={!newItemYear}>
            <InputLabel>Year</InputLabel>
            <Select
              value={newItemYear}
              onChange={(e) => setNewItemYear(e.target.value)}
              label="Year"
              variant="standard"
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select year</em>
              </MenuItem>
              {tableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
              {/* Include current year if not already in the list */}
              {!tableYears.includes(new Date().getFullYear()) && (
                <MenuItem value={new Date().getFullYear().toString()}>
                  {new Date().getFullYear()}
                </MenuItem>
              )}
              <MenuItem value="custom" divider>
                <em>Custom year...</em>
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            error={!!newItemAmount && isNaN(parseFloat(newItemAmount))}
            helperText={!!newItemAmount && isNaN(parseFloat(newItemAmount)) ? 'Must be a valid number' : ''}
            label="Amount"
            value={newItemAmount}
            onChange={(e) => setNewItemAmount(e.target.value)}
            type="number"
            variant="standard"
            sx={{ width: 120 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleAddNewItem}
              disabled={!selectedCategory || !newItemYear || !newItemAmount || loading}
              startIcon={<AddIcon />}
              sx={{
                height: 36,
                whiteSpace: 'nowrap',
              }}
            >
              {loading ? 'Adding...' : 'Add Item'}
            </Button>

            <Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setTabValue(1)}
                startIcon={<ChartIcon />}
                sx={{ mr: 2 }}
              >
                View Financial Tables
              </Button>
            </Box>
          </Box>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <MetricsTable
          metrics={metrics}
          tableYears={tableYears}
          editedData={editedData}
          onValueChange={handleValueChange}
          onDeleteItem={handleDeleteItem}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {metrics.length > 0 ? (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                Below are the consolidated financial tables for your hotel deal, showing all key metrics in organized views.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                These tables are automatically generated from the financial data you've entered. To add or edit data, use the "Data Entry" tab.
              </Alert>
            </Box>
            {/* Ensure metrics values are transformed to numbers if they're strings */}
            <DealFinancialsTables
              metrics={metrics.map(metric => ({
                ...metric,
                value: typeof metric.value === 'string' ?
                  parseFloat(metric.value.replace(/[^0-9.-]/g, '')) || 0 :
                  metric.value
              }))}
              years={tableYears.map(year => typeof year === 'string' ? parseInt(year, 10) : year)}
            />
          </>
        ) : (
          <Alert severity="info">No financial data has been added yet. Please add data in the Data Entry tab.</Alert>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Financial Performance Summary
          </Typography>

          {financialSummary.year && (
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
              Showing data for FY {financialSummary.year}
            </Typography>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  Revenue & Income
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Total Revenue</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {financialSummary.revenue !== undefined
                      ? formatCurrency(financialSummary.revenue)
                      : 'No data'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Total Expenses</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {financialSummary.expenses !== undefined
                      ? formatCurrency(financialSummary.expenses)
                      : 'No data'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Net Operating Income</Typography>
                  <Typography variant="body2" fontWeight="medium" color="success.main">
                    {financialSummary.netOperatingIncome !== undefined
                      ? formatCurrency(financialSummary.netOperatingIncome)
                      : 'No data'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Profit Margin</Typography>
                  <Typography variant="body2" fontWeight="medium" color="success.main">
                    {financialSummary.profitMargin !== undefined
                      ? formatPercent(financialSummary.profitMargin)
                      : 'No data'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  Key Performance Indicators
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">Occupancy Rate</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {financialSummary.occupancyRate !== undefined
                      ? formatPercent(financialSummary.occupancyRate)
                      : 'No data'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">ADR (Average Daily Rate)</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {financialSummary.adr !== undefined
                      ? formatCurrency(financialSummary.adr)
                      : 'No data'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2">RevPAR (Revenue Per Available Room)</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {financialSummary.revpar !== undefined
                      ? formatCurrency(financialSummary.revpar)
                      : 'No data'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="textSecondary">
              This financial summary is based on the data you've entered. Complete the financial data entry for a more comprehensive analysis.
            </Typography>
          </Box>
        </Paper>

        {/* Quick actions */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setTabValue(0)}
            startIcon={<AddIcon />}
          >
            Add More Data
          </Button>

          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => setTabValue(3)}
            startIcon={<ChartIcon />}
          >
            View by Category
          </Button>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {Object.keys(metricsByCategory).length > 0 ? (
          Object.entries(metricsByCategory)
            .sort(([a], [b]) => {
              // Sort by category order as defined in metricGroupedCategories
              const orderA = metricGroupedCategories[a]?.order || 999;
              const orderB = metricGroupedCategories[b]?.order || 999;
              return orderA - orderB;
            })
            .map(([category, categoryMetrics]) => (
              <Box key={category} sx={{ mb: 4 }}>
                <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    {category}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <MetricsTable
                    metrics={categoryMetrics}
                    tableYears={tableYears}
                    editedData={editedData}
                    onValueChange={handleValueChange}
                    onDeleteItem={handleDeleteItem}
                  />
                </Paper>
              </Box>
            ))
        ) : (
          <Alert severity="info">No financial data has been added yet.</Alert>
        )}
      </TabPanel>
    </Box>
  );
};

export default FinancialDataStep;